/**
 * MapPicker Component
 * Interactive map for selecting GPS coordinates
 * Uses OpenStreetMap + Leaflet (free, no API key required)
 */

import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet marker icon issue in Vite/Webpack
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

interface MapPickerProps {
  latitude?: number;
  longitude?: number;
  onLocationSelect: (lat: number, lng: number) => void;
  height?: string;
}

// Component for handling map clicks
function LocationMarker({ position, setPosition, onLocationSelect }: {
  position: [number, number];
  setPosition: (pos: [number, number]) => void;
  onLocationSelect: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click(e) {
      const newPos: [number, number] = [e.latlng.lat, e.latlng.lng];
      setPosition(newPos);
      // Notify parent only when user clicks the map
      onLocationSelect(newPos[0], newPos[1]);
    },
  });

  return position ? <Marker position={position} /> : null;
}

export const MapPicker: React.FC<MapPickerProps> = ({
  latitude = 13.7563,  // Default: Bangkok center
  longitude = 100.5018,
  onLocationSelect,
  height = '400px'
}) => {
  const [position, setPosition] = useState<[number, number]>([latitude, longitude]);

  // Update position when props change from parent
  // FIX: Only update internal state, don't trigger onLocationSelect
  useEffect(() => {
    if (latitude && longitude) {
      setPosition([latitude, longitude]);
    }
  }, [latitude, longitude]);

  return (
    <div className="space-y-2">
      <MapContainer
        center={position}
        zoom={13}
        style={{ height, width: '100%', borderRadius: '8px' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <LocationMarker
          position={position}
          setPosition={setPosition}
          onLocationSelect={onLocationSelect}
        />
      </MapContainer>

      <div className="flex items-center justify-between text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded">
        <span className="font-medium">พิกัดที่เลือก:</span>
        <span className="font-mono">
          {position[0].toFixed(8)}, {position[1].toFixed(8)}
        </span>
      </div>

      <p className="text-xs text-gray-500">
        คลิกบนแผนที่เพื่อเลือกตำแหน่ง หรือใช้ปุ่ม "ใช้ตำแหน่งปัจจุบัน" ด้านบน
      </p>
    </div>
  );
};

export default MapPicker;
